/**
 * useMapPlacementMode - Hook for aircraft placement mode on the map
 *
 * Follows the building placement pattern from useBuildingDragInteraction.ts:
 * - Loads actual GLTF aircraft model (not simplified ghost geometry)
 * - Model follows cursor until placement confirmed
 * - Click detection via mousedown/mouseup (duration < 300ms, distance < 10px)
 * - Shift+Scroll rotates aircraft (normal scroll zooms map)
 * - ESC to cancel
 * - Map panning/zooming allowed during placement
 */

import { useEffect, useRef, useCallback } from 'react'
import * as THREE from 'three'
import { useAddPlaneWizardStore } from '@/stores/addPlaneWizardStore'
import { useAircraftType } from '@/hooks/useAircraftTypes'
import { detectHangarForPosition } from '@/lib/utils/hangar-detection'
import { screenToWorldPosition } from '@/utils/coordinates'
import { FEET_TO_METERS, METERS_TO_FEET } from '@/constants/units'
import { getAircraftLoaderInstance, type PlacedAircraft3D, type ModelSettings } from '@/services/3d'
import type { Building3DConfig } from '@/services/3d'
import { ProceduralAircraftFactory } from '@/services/3d/procedural'

interface UseMapPlacementModeOptions {
  /** Map instance - passed directly like useAircraftDragInteraction */
  map: mapboxgl.Map | null
  /** Scene objects for 3D operations */
  sceneObjects: {
    scene: THREE.Scene | null
    camera: THREE.Camera | null
  } | null
  /** Scene center [lng, lat] - MUST match the center used when creating the 3D layer */
  sceneCenter: [number, number] | null
  /** Buildings/areas for area detection */
  buildings: Building3DConfig[]
  /** Called when placement is confirmed */
  onPlacementConfirm?: () => void
  /** Called when placement is cancelled */
  onPlacementCancel?: () => void
}

interface PlacementState {
  previewAircraft: THREE.Object3D | null
  currentRotation: number
  aircraftLoader: AircraftLoaderService | null
  hasInitialPosition: boolean  // Track if we've positioned to cursor yet
  modelOrigin: [number, number] | null  // Fixed lng/lat origin for coordinate conversion
}

export function useMapPlacementMode({
  map,
  sceneObjects,
  sceneCenter,
  buildings,
  onPlacementConfirm,
  onPlacementCancel,
}: UseMapPlacementModeOptions) {
  const {
    isPlacementModeActive,
    selectedAircraftTypeId,
    aircraftDimensions,
  } = useAddPlaneWizardStore()

  // Fetch aircraft type for model settings
  const { data: aircraftType } = useAircraftType(selectedAircraftTypeId || undefined)

  // Track placement state
  const stateRef = useRef<PlacementState>({
    previewAircraft: null,
    currentRotation: 0,
    aircraftLoader: null,
    hasInitialPosition: false,
    modelOrigin: null,
  })

  // Pre-allocated Vector2 to avoid per-event allocations
  const tempVec2Ref = useRef(new THREE.Vector2())

  // Intermediate buffer — position/rotation/detection stored here during mousemove.
  // Store is only updated on placement confirm (click) to avoid per-frame re-renders.
  const intermediateRef = useRef<{
    positionFeet: [number, number, number]
    rotation: number
    areaId: string | null
    unitId: string | null
  }>({ positionFeet: [0, 0, 0], rotation: 0, areaId: null, unitId: null })
  // Debounce hangar detection to 100ms (expensive operation)
  const lastDetectionTimeRef = useRef(0)

  // Click detection refs (following building placement pattern)
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null)
  const mouseDownTimeRef = useRef<number>(0)
  const wasDragRef = useRef(false)

  // Touch-specific refs
  /** Skip first touchend after activation (same as skipNextClickRef for mouse) */
  const skipNextTouchRef = useRef(false)
  /** Two-finger twist state */
  const isTwoFingerTwistRef = useRef(false)
  const twoFingerStartAngleRef = useRef<number | null>(null)
  const twoFingerStartRotationRef = useRef<number | null>(null)
  /** Timestamp of last touch — suppress synthesized click events */
  const lastTouchTimeRef = useRef(0)

  // Store refs for values needed in event handlers (to avoid stale closures)
  const buildingsRef = useRef(buildings)
  buildingsRef.current = buildings

  // Store callback refs to avoid effect re-running when callbacks change
  const onPlacementConfirmRef = useRef(onPlacementConfirm)
  onPlacementConfirmRef.current = onPlacementConfirm
  const onPlacementCancelRef = useRef(onPlacementCancel)
  onPlacementCancelRef.current = onPlacementCancel

  // Store map in a ref so it's available even when prop is temporarily null
  // This handles the case where sceneRefs becomes null during React re-renders
  const mapRef = useRef<mapboxgl.Map | null>(null)
  if (map) {
    mapRef.current = map
  }

  // RAF-batched repaint to avoid per-event triggerRepaint spam during mousemove/wheel
  const repaintScheduledRef = useRef(false)
  const scheduleRepaint = useCallback(() => {
    if (repaintScheduledRef.current) return
    repaintScheduledRef.current = true
    requestAnimationFrame(() => {
      repaintScheduledRef.current = false
      mapRef.current?.triggerRepaint()
    })
  }, [])

  // Guard ref to prevent multiple event listener attachments per placement session
  const attachedRef = useRef(false)

  // Skip the first click after activation (the click that triggered "Place on Map")
  // This prevents the activation click from immediately confirming placement
  const skipNextClickRef = useRef(false)

  // Get shared aircraft loader instance (may have preloaded models from AircraftStep)
  useEffect(() => {
    if (!stateRef.current.aircraftLoader) {
      stateRef.current.aircraftLoader = getAircraftLoaderInstance()
    }
  }, [])

  // Load actual aircraft model when entering placement mode
  useEffect(() => {
    if (!isPlacementModeActive || !sceneObjects?.scene) {
      return
    }

    // If no aircraft type selected yet, create a simple placeholder
    if (!aircraftType) {
      console.log('useMapPlacementMode: No aircraft type selected yet, using placeholder')
      const placeholder = createPlaceholderMesh(aircraftDimensions)
      placeholder.name = 'preview-aircraft'
      // Position off-screen initially
      placeholder.position.set(-99999, 0.1, -99999)
      stateRef.current.hasInitialPosition = false
      sceneObjects.scene.add(placeholder)
      stateRef.current.previewAircraft = placeholder
      map?.triggerRepaint()
      return
    }

    // Construct ModelSettings from AircraftTypeRow fields (not model_settings)
    const effectiveModelSettings: ModelSettings = {
      filePath: aircraftType.model_url || undefined,
      scale: aircraftType.model_scale || 1,
      headingAxis: (aircraftType.heading_axis as 'x' | 'y' | 'z') || 'y',
      verticalAxis: (aircraftType.vertical_axis as 'x' | 'y' | 'z') || 'y',
    }

    // Build physical dimensions for procedural fallback
    const physicalDimensions =
      aircraftType.length && aircraftType.wingspan && aircraftType.height
        ? {
            length: aircraftType.length,
            wingspan: aircraftType.wingspan,
            height: aircraftType.height,
            tailHeight: aircraftType.tail_height ?? null,
            category: aircraftType.category ?? null,
            gearType: aircraftType.gear_type ?? null,
          }
        : undefined

    // Create preview aircraft data structure
    const previewAircraft: PlacedAircraft3D = {
      id: 'preview-aircraft',
      aircraftId: 'preview',
      tailNumber: 'PREVIEW',
      typeId: aircraftType.id,
      position: [0, 0, 0], // Will be updated by cursor tracking
      rotation: 0,
      scale: 1,
      aircraftType: {
        id: aircraftType.id,
        typeCode: aircraftType.type_code,
        modelSettings: effectiveModelSettings,
      },
      physicalDimensions,
    }

    // Load the GLTF model
    const loader = stateRef.current.aircraftLoader
    if (!loader) {
      console.warn('useMapPlacementMode: No aircraft loader available')
      return
    }

    console.log('useMapPlacementMode: Loading aircraft model for', aircraftType.type_code, 'model_url:', aircraftType.model_url)

    loader.loadAircraft(previewAircraft).then((model) => {
      // Check if placement mode is still active (could have been cancelled during load)
      if (!useAddPlaneWizardStore.getState().isPlacementModeActive) {
        console.log('useMapPlacementMode: Placement cancelled during model load')
        return
      }

      // Apply semi-transparent material for preview effect
      model.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          const material = child.material.clone()
          if (material instanceof THREE.MeshStandardMaterial || material instanceof THREE.MeshPhysicalMaterial) {
            material.transparent = true
            material.opacity = 0.7
          }
          child.material = material
        }
      })

      model.name = 'preview-aircraft'

      // Remove any existing preview
      if (stateRef.current.previewAircraft && sceneObjects?.scene) {
        sceneObjects.scene.remove(stateRef.current.previewAircraft)
      }

      // Position off-screen initially - first mouse move will place it at cursor
      // This avoids the offset issue where model starts at origin
      model.position.set(-99999, 0.1, -99999)
      stateRef.current.hasInitialPosition = false

      sceneObjects?.scene?.add(model)
      stateRef.current.previewAircraft = model

      console.log('useMapPlacementMode: Aircraft model loaded and added to scene at position:', model.position.toArray())
      map?.triggerRepaint()
    }).catch((error) => {
      console.error('useMapPlacementMode: Error loading aircraft model:', error)
      // Fallback to placeholder on error
      const placeholder = createPlaceholderMesh(aircraftDimensions)
      placeholder.name = 'preview-aircraft'
      // Position off-screen initially
      placeholder.position.set(-99999, 0.1, -99999)
      stateRef.current.hasInitialPosition = false
      sceneObjects?.scene?.add(placeholder)
      stateRef.current.previewAircraft = placeholder
      map?.triggerRepaint()
    })

    // Cleanup on exit
    return () => {
      if (stateRef.current.previewAircraft && sceneObjects?.scene) {
        sceneObjects.scene.remove(stateRef.current.previewAircraft)
        stateRef.current.previewAircraft = null
        stateRef.current.hasInitialPosition = false
        map?.triggerRepaint()
      }
    }
  }, [isPlacementModeActive, map, sceneObjects, aircraftType, aircraftDimensions])

  // Attach event listeners when in placement mode
  // Uses DOCUMENT-LEVEL events instead of Mapbox events for reliability
  useEffect(() => {
    // Use mapRef.current which persists even when map prop becomes null during re-renders
    const currentMap = mapRef.current

    // Reset guards when not in placement mode
    if (!isPlacementModeActive) {
      attachedRef.current = false
      skipNextClickRef.current = false
      skipNextTouchRef.current = false
      return
    }

    // Need map and sceneCenter to proceed
    if (!currentMap || !sceneCenter) {
      return
    }

    // Guard: Only attach once per placement session to prevent spam
    if (attachedRef.current) {
      return
    }
    attachedRef.current = true

    // Skip the next click/touch (the one that triggered activation)
    // This prevents the "Place on Map" button click/tap from immediately confirming
    skipNextClickRef.current = true
    skipNextTouchRef.current = true

    const canvas = currentMap.getCanvas()
const modelOrigin: [number, number] = sceneCenter
    stateRef.current.modelOrigin = modelOrigin

    // DOCUMENT-LEVEL MOUSEMOVE (always fires, unlike map.on)
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Only update if cursor is over the map canvas
      if (x < 0 || x > rect.width || y < 0 || y > rect.height) return

      // Convert to NDC (-1 to 1) — reuse pre-allocated vector
      const mouse = tempVec2Ref.current.set(
        (x / canvas.clientWidth) * 2 - 1,
        -(y / canvas.clientHeight) * 2 + 1
      )

      // Get world position using fixed model origin
      const worldPos = screenToWorldPosition(mouse, currentMap, modelOrigin)
      if (!worldPos) return

      // Update preview aircraft position
      const preview = stateRef.current.previewAircraft
      if (preview) {
        preview.position.set(worldPos.x, 0.1, worldPos.z)
        scheduleRepaint()
      }

      // Convert to feet and buffer in ref (no store update per frame)
      const positionFeet: [number, number, number] = [
        worldPos.x * METERS_TO_FEET,
        worldPos.y * METERS_TO_FEET,
        worldPos.z * METERS_TO_FEET,
      ]
      intermediateRef.current.positionFeet = positionFeet
      intermediateRef.current.rotation = stateRef.current.currentRotation

      // Debounced hangar detection (100ms) — expensive operation, not needed per frame
      const now = Date.now()
      if (now - lastDetectionTimeRef.current > 100) {
        lastDetectionTimeRef.current = now
        const detection = detectHangarForPosition(positionFeet, buildingsRef.current)
        intermediateRef.current.areaId = detection.areaId
        intermediateRef.current.unitId = detection.unitId
      }
    }

    // DOCUMENT-LEVEL CLICK for placement confirmation
    const handleClick = (e: MouseEvent) => {
      // Suppress synthesized click events from touch
      if (Date.now() - lastTouchTimeRef.current < 500) return

      // Skip the click that triggered placement mode activation
      if (skipNextClickRef.current) {
        skipNextClickRef.current = false
        return
      }

      const rect = canvas.getBoundingClientRect()
      // Only confirm if click is on the map canvas
      if (e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom) {
        confirmPlacement()
      }
    }

    /** Shared confirm logic — used by both click and touchend */
    const confirmPlacement = () => {
      const buf = intermediateRef.current
      const store = useAddPlaneWizardStore.getState()
      store.updatePlacementPosition(
        { x: buf.positionFeet[0], y: buf.positionFeet[1], z: buf.positionFeet[2] },
        buf.rotation
      )
      // Run final detection (unbounced) so store has accurate area/unit
      const detection = detectHangarForPosition(buf.positionFeet, buildingsRef.current)
      store.updateDetectedLocation(detection.areaId, detection.unitId)
      store.confirmPlacement()
      onPlacementConfirmRef.current?.()
    }

    // Handle wheel - Shift+Scroll rotates, normal scroll zooms
    const handleWheel = (e: WheelEvent) => {
      // Only rotate when Shift is held - otherwise let Mapbox zoom
      if (!e.shiftKey) return

      e.preventDefault()
      e.stopPropagation()

      // Rotate by 5 degrees per scroll tick
      const rotationDelta = e.deltaY > 0 ? 5 : -5
      stateRef.current.currentRotation = (stateRef.current.currentRotation + rotationDelta + 360) % 360

      // Update preview rotation
      const preview = stateRef.current.previewAircraft
      if (preview) {
        preview.rotation.y = (stateRef.current.currentRotation * Math.PI) / 180
        scheduleRepaint()
      }

      // Buffer rotation — store is updated on click confirm
      intermediateRef.current.rotation = stateRef.current.currentRotation
    }

    // TOUCH HANDLERS — preview follows finger, tap to confirm, two-finger twist to rotate

    const handleTouchStart = (e: TouchEvent) => {
      lastTouchTimeRef.current = Date.now()

      // Two-finger twist for rotation
      if (e.touches.length >= 2) {
        // Cancel any in-progress single-finger interaction
        isTwoFingerTwistRef.current = true
        twoFingerStartAngleRef.current = Math.atan2(
          e.touches[1].clientY - e.touches[0].clientY,
          e.touches[1].clientX - e.touches[0].clientX
        )
        twoFingerStartRotationRef.current = stateRef.current.currentRotation
        e.preventDefault()
        return
      }

      // Single touch — record position for tap detection
      mouseDownPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
      mouseDownTimeRef.current = Date.now()
      wasDragRef.current = false
    }

    const handleTouchMove = (e: TouchEvent) => {
      lastTouchTimeRef.current = Date.now()

      // Two-finger twist rotation
      if (isTwoFingerTwistRef.current && e.touches.length >= 2) {
        const currentAngle = Math.atan2(
          e.touches[1].clientY - e.touches[0].clientY,
          e.touches[1].clientX - e.touches[0].clientX
        )
        const deltaAngle = currentAngle - (twoFingerStartAngleRef.current ?? 0)
        const deltaDegrees = (deltaAngle * 180) / Math.PI
        const newRotation = ((twoFingerStartRotationRef.current ?? 0) - deltaDegrees + 360) % 360

        stateRef.current.currentRotation = newRotation
        const preview = stateRef.current.previewAircraft
        if (preview) {
          preview.rotation.y = (newRotation * Math.PI) / 180
          scheduleRepaint()
        }
        intermediateRef.current.rotation = newRotation
        e.preventDefault()
        return
      }

      // Single finger — preview follows finger
      if (e.touches.length === 1) {
        const touch = e.touches[0]
        const rect = canvas.getBoundingClientRect()
        const x = touch.clientX - rect.left
        const y = touch.clientY - rect.top

        // Only update if finger is over the map canvas
        if (x < 0 || x > rect.width || y < 0 || y > rect.height) return

        // Track drag distance for tap detection
        if (mouseDownPosRef.current) {
          const dx = touch.clientX - mouseDownPosRef.current.x
          const dy = touch.clientY - mouseDownPosRef.current.y
          if (Math.sqrt(dx * dx + dy * dy) > 10) {
            wasDragRef.current = true
          }
        }

        const mouse = tempVec2Ref.current.set(
          (x / canvas.clientWidth) * 2 - 1,
          -(y / canvas.clientHeight) * 2 + 1
        )

        const worldPos = screenToWorldPosition(mouse, currentMap, modelOrigin)
        if (!worldPos) return

        const preview = stateRef.current.previewAircraft
        if (preview) {
          preview.position.set(worldPos.x, 0.1, worldPos.z)
          scheduleRepaint()
        }

        const positionFeet: [number, number, number] = [
          worldPos.x * METERS_TO_FEET,
          worldPos.y * METERS_TO_FEET,
          worldPos.z * METERS_TO_FEET,
        ]
        intermediateRef.current.positionFeet = positionFeet
        intermediateRef.current.rotation = stateRef.current.currentRotation

        const now = Date.now()
        if (now - lastDetectionTimeRef.current > 100) {
          lastDetectionTimeRef.current = now
          const detection = detectHangarForPosition(positionFeet, buildingsRef.current)
          intermediateRef.current.areaId = detection.areaId
          intermediateRef.current.unitId = detection.unitId
        }
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      lastTouchTimeRef.current = Date.now()

      // End two-finger twist
      if (isTwoFingerTwistRef.current) {
        isTwoFingerTwistRef.current = false
        twoFingerStartAngleRef.current = null
        twoFingerStartRotationRef.current = null
        return
      }

      // Skip the touch that triggered placement mode activation
      if (skipNextTouchRef.current) {
        skipNextTouchRef.current = false
        mouseDownPosRef.current = null
        return
      }

      // Tap detection: short duration + small movement = confirm placement
      if (mouseDownPosRef.current && !wasDragRef.current) {
        const touch = e.changedTouches[0]
        if (touch) {
          const duration = Date.now() - mouseDownTimeRef.current
          const dx = touch.clientX - mouseDownPosRef.current.x
          const dy = touch.clientY - mouseDownPosRef.current.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          const rect = canvas.getBoundingClientRect()
          const isOverCanvas = touch.clientX >= rect.left && touch.clientX <= rect.right &&
            touch.clientY >= rect.top && touch.clientY <= rect.bottom

          if (duration < 300 && distance < 15 && isOverCanvas) {
            confirmPlacement()
          }
        }
      }

      mouseDownPosRef.current = null
      mouseDownTimeRef.current = 0
      wasDragRef.current = false
    }

    const handleTouchCancel = () => {
      lastTouchTimeRef.current = Date.now()
      isTwoFingerTwistRef.current = false
      twoFingerStartAngleRef.current = null
      twoFingerStartRotationRef.current = null
      mouseDownPosRef.current = null
      mouseDownTimeRef.current = 0
      wasDragRef.current = false
    }

    // Handle ESC to cancel
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        useAddPlaneWizardStore.getState().cancelPlacement()
        onPlacementCancelRef.current?.()
      }
    }

    // Attach DOCUMENT-LEVEL listeners (these always fire, unlike Mapbox events)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('click', handleClick)
    document.addEventListener('keydown', handleKeyDown)
    canvas.addEventListener('wheel', handleWheel, { passive: false })

    // Touch listeners — passive: false to allow preventDefault during two-finger twist
    document.addEventListener('touchstart', handleTouchStart, { passive: false })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)
    document.addEventListener('touchcancel', handleTouchCancel)

    // Change cursor to crosshair
    canvas.style.cursor = 'crosshair'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('click', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
      canvas.removeEventListener('wheel', handleWheel)
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('touchcancel', handleTouchCancel)
      canvas.style.cursor = ''
      // Clear origin on cleanup
      stateRef.current.modelOrigin = null
      attachedRef.current = false
      // Reset touch state
      isTwoFingerTwistRef.current = false
      twoFingerStartAngleRef.current = null
      twoFingerStartRotationRef.current = null
    }
  }, [isPlacementModeActive, sceneCenter])  // Uses mapRef.current which persists across re-renders

  return {
    isPlacementModeActive,
  }
}

/**
 * Create a placement preview model.
 * Uses procedural aircraft shape when dimensions are available,
 * falls back to a simple box when they're not.
 */
function createPlaceholderMesh(
  dimensions: { wingspan: number | null; length: number | null; height?: number | null; tail_height?: number | null } | null
): THREE.Object3D {
  // Use procedural model when we have enough dimensions
  if (dimensions?.length && dimensions?.wingspan && dimensions?.height) {
    const group = ProceduralAircraftFactory.create({
      length: dimensions.length,
      wingspan: dimensions.wingspan,
      height: dimensions.height,
      tailHeight: dimensions.tail_height ?? null,
      category: null,
      gearType: null,
    })
    // Make semi-transparent for preview effect
    group.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const mat = (child.material as THREE.MeshStandardMaterial).clone()
        mat.transparent = true
        mat.opacity = 0.7
        child.material = mat
      }
    })
    group.castShadow = true
    return group
  }

  // Fallback: simple box placeholder
  const wingspan = (dimensions?.wingspan ?? 35) * FEET_TO_METERS
  const length = (dimensions?.length ?? 27) * FEET_TO_METERS
  const height = 2 * FEET_TO_METERS

  const geometry = new THREE.BoxGeometry(wingspan, height, length)
  const material = new THREE.MeshStandardMaterial({
    color: 0x3b82f6,
    transparent: true,
    opacity: 0.7,
    side: THREE.DoubleSide,
  })

  const mesh = new THREE.Mesh(geometry, material)
  mesh.castShadow = true
  mesh.receiveShadow = false
  return mesh
}

export default useMapPlacementMode
